import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { fetchHseSettings } from "../../../api/hse/settings";
import { fetchHseSetupStatus } from "../../../api/hse/setupStatus";
import { hseKsSetupIssues } from "../companyGuide/hseKsSetupStatus";
import { viewerCanSeeHseSetupBanner } from "./hseCompanySettingsUtils";

export function HseSetupMissingBanner({ settings: settingsProp }) {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isAdmin = useUserHasAccess({ role: "admin" });
  const [settings, setSettings] = useState(settingsProp || null);
  const [setup, setSetup] = useState(settingsProp?.setup || null);

  useEffect(() => {
    if (settingsProp) {
      setSettings(settingsProp);
      setSetup(settingsProp.setup || null);
    }
  }, [settingsProp]);

  useEffect(() => {
    if (settingsProp) return undefined;

    let cancelled = false;
    Promise.all([
      fetchHseSetupStatus().catch(() => null),
      fetchHseSettings().catch(() => null),
    ]).then(([setupData, settingsData]) => {
      if (cancelled) return;
      setSetup(setupData);
      setSettings(settingsData);
    });
    return () => {
      cancelled = true;
    };
  }, [settingsProp]);

  const issues = hseKsSetupIssues(setup || settings || {});
  const visible = settings && viewerCanSeeHseSetupBanner(currentUser, settings, isAdmin);

  if (!visible || issues.length === 0) return null;

  return (
    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{t("hse.setup.banner.title")}</p>
          <p className="mt-1 text-sm text-amber-900/80">
            {t("hse.setup.banner.subtitle", { count: issues.length })}
          </p>
          <div className="mt-2 flex flex-wrap gap-3 text-sm font-semibold">
            <Link
              to="/dashboard/hse-ks/settings?tab=safety_rep"
              className="underline decoration-amber-300 underline-offset-4 hover:decoration-amber-600"
            >
              {t("hse.setup.banner.settings")}
            </Link>
            <Link
              to="/dashboard/guide/hse-ks#hse-ks-setup-status"
              className="underline decoration-amber-300 underline-offset-4 hover:decoration-amber-600"
            >
              {t("hse.setup.banner.guide")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
